// Implements: F-UIX-019, F-UIX-007, F-GEN-036
/**
 * Where the stored model settings come from (source, revision, author, when), as one meta line
 * above the form. The cost per turn lives in the action bar (`CostLine`) and the technical
 * limits in their own read-only card.
 */

import type { ReactElement } from "react";

import { useTranslation } from "../../../shared/i18n";
import { Button } from "../../../shared/ui/atoms/Button";
import type { ModelSettingsView } from "../../../types/modelSettings";
import { sourceKey } from "../model/sourceLabel";
import { instantText } from "../model/status";
import styles from "./ModelSettings.module.css";

export interface ModelSettingsSummaryProps {
  readonly settings: ModelSettingsView;
}

export function ModelSettingsSummary({ settings }: ModelSettingsSummaryProps): ReactElement {
  const { t } = useTranslation();
  const at = instantText(settings.updated_at);
  const key = sourceKey(settings.source);
  const sourceName = key === null ? settings.source : t(key);
  return (
    <p className={styles.summary}>
      <span>{t("admin.modelSettings.source", { source: sourceName })}</span>
      <span>{t("admin.modelSettings.revision", { revision: String(settings.revision) })}</span>
      {at === null ? null : (
        <span>
          {settings.updated_by_name === null
            ? t("admin.modelSettings.updatedNoAuthor", { at })
            : t("admin.modelSettings.updated", { at, name: settings.updated_by_name })}
        </span>
      )}
    </p>
  );
}

export interface CostLineProps {
  /** The server's figure for the stored settings, or `null` when it cannot be computed. */
  readonly stored: number | null;
  /**
   * The preview of the edits on screen: `undefined` when none was asked for these values,
   * `"pending"` while it loads, `"failed"` when it could not be computed, else its figure.
   */
  readonly preview: number | "pending" | "failed" | undefined;
  /** The edits can be previewed (they changed and they parse). */
  readonly canPreview: boolean;
  readonly onPreview: () => void;
  /** Formats an amount of micros (the module's own money formatter). */
  readonly money: (micros: number) => string;
}

/** "Costo máximo estimado por turno: <amount>", the stored one beside a preview, and the preview button. */
export function CostLine({ stored, preview, canPreview, onPreview, money }: CostLineProps): ReactElement {
  const { t } = useTranslation();
  const storedText = stored === null ? t("admin.modelSettings.cost.none") : money(stored);
  const previewed = typeof preview === "number";
  return (
    <>
      <p className={styles.cost} aria-live="polite">
        {t("admin.modelSettings.cost.label")} <b className={styles.amount}>{previewed ? money(preview) : storedText}</b>
        {previewed ? (
          <>
            {" "}
            <span className={styles.meta}>{t("admin.modelSettings.cost.before", { amount: storedText })}</span>
          </>
        ) : null}
        {preview === "failed" ? (
          <>
            {" "}
            <span className={styles.meta}>{t("admin.modelSettings.cost.failed")}</span>
          </>
        ) : null}
      </p>
      {canPreview && (preview === undefined || preview === "pending") ? (
        <span className={styles.previewButton}>
          <Button variant="ghost" size="sm" loading={preview === "pending"} onClick={onPreview}>
            {t("admin.modelSettings.cost.preview")}
          </Button>
        </span>
      ) : null}
    </>
  );
}
